import { Contact, ContactFields, ValidationError } from '@api';
import classnames from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { change, reduxForm } from 'redux-form';
import { InjectedFormProps } from 'redux-form/lib/reduxForm';
import { sendAdobeAnalyticsEvent } from 'src/js/actions/AdobeAnalyticActions';
import { scrollToTop, scrollToValidation } from 'src/js/common/utils/DomUtils';
import { getFormInitialValues, toFormKey } from 'src/js/components/form/FieldFactory';
import { AdobeAnalyticsFormAbandonTrackService } from 'src/js/services/AdobeAnalyticsService';
import { IshState } from 'src/js/services/IshState';
import { changePhase, showFormValidation, showSyncErrors } from '../../actions/Actions';
import { ContactAddProcessState, Phase } from '../../reducers/State';
import CheckoutService from '../../services/CheckoutService';
import { submitEditContact } from './actions/Actions';
import { ContactEdit } from './components/ContactEdit';

export const NAME = 'ContactEditForm';

class ContactEditForm extends React.Component<Props, any> {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    scrollToTop();
    AdobeAnalyticsFormAbandonTrackService.attachFormAbandonClicksTrack(this.props.form, this.props.dispatch)
  }

  componentWillUnmount() {
    AdobeAnalyticsFormAbandonTrackService.detachFormAbandonClicksTrack()
  }

  render() {
    const {handleSubmit, touch, submitting, isNewContact, onCancel, page, dispatch, form, onChangeSuburb} = this.props;
    const contact: Contact = this.props.contactAddProcess.contact;
    const fields: ContactFields = this.props.fields;

    return (
      <div>
        <form onSubmit={handleSubmit} id="contactEditorForm" className={classnames({submitting})}>
          <ContactEdit
            touch={touch}
            contact={contact}
            fields={fields}
            onChangeSuburb={item => onChangeSuburb(form, item, fields)}
            form={form}
            dispatch={dispatch}
          />

          <div className="form-controls flex">
            <span>
            {!isNewContact &&
              <a
                href="#"
                type="button"
                onClick={() => onCancel(page)}
              >
                Cancel
              </a>
            }
            </span>

            <input
              value="OK"
              className="btn btn-primary"
              name="submitContact"
              type="submit"
              disabled={submitting}
            />
          </div>
        </form>
      </div>
    );
  }
}

interface Props extends InjectedFormProps {
  contactAddProcess: ContactAddProcessState;
  fields: ContactFields;
  errors: ValidationError;
  onInit: () => void;
  onCancel: (page) => void;
  concessionTypes: any;
  isNewContact: boolean;
  page: number;
  onChangeSuburb?: (form, item, fields) => void;
  dispatch?: Dispatch;
}

const Form = reduxForm({
  form: NAME,
  validate: (data, props: Props) => {
    const errors = {};
    props.fields && props.fields.headings.map(headings =>
      headings.fields.map((field, index) =>
        (field.mandatory && (field.dataType !== 'BOOLEAN' || field.key === 'isMale') && !data[toFormKey(field.key, index)])
          ? errors[toFormKey(field.key, index)] = `Field '${field.name}' is required`
          : field,
      ),
    );
    return {...errors, ...validateAge(data, props)};
  },
  onSubmitSuccess: (result, dispatch, props: any) => {
    dispatch(submitEditContact({
      ...props.contactAddProcess.contact,
      parentRequired:
      result.parentRequired,
    }));
    dispatch(sendAdobeAnalyticsEvent('your details – step 2'));
  },
  onSubmitFail: (errors, dispatch, submitError) => {
    if (errors && !submitError) {
      dispatch(showSyncErrors(errors));
    } else if (submitError && submitError.data) {
      dispatch(showFormValidation({
        ...submitError,
        data: { validationErrors: submitError.data }
      },
      NAME));
    }
    setTimeout(() => scrollToValidation(), 50);
  },
})(ContactEditForm);

const validateAge = (data, props) => {
  // validate age if contact is a guardian or parent
  if (props.contactAddProcess && (props.contactAddProcess.type === Phase.AddParent || props.contactAddProcess.type === Phase.ChangeParent)) {
    if (!data.dateOfBirth) return {};

    const dd = data.dateOfBirth.split('/')[0];
    const mm = data.dateOfBirth.split('/')[1];
    const yyyy = data.dateOfBirth.split('/')[2];
    const age = new Date().getFullYear() - new Date(`${mm}/${dd}/${yyyy}`).getFullYear();

    if (age < 18) {
      return {
        dateOfBirth: 'Guardian must be over 18',
      };
    }
  }

  return {};
};

const mapStateToProps = (state: IshState) => {
  // state.checkout.contacts.entities.contact[state.checkout.fields.contactId]
  const contactAddProcess = state.checkout.contactAddProcess;
  const fields = state.checkout.fields.current;
  const errors = state.checkout.error;
  const concessionTypes = state.checkout.concession.types;
  const isNewContact = !state.checkout.contacts.result.length;
  const page = state.checkout.page;
  const contactId = state.checkout.contactAddProcess.contact.id;

  const initialValues =  getFormInitialValues(fields?.headings);

  return {
    contactAddProcess,
    fields,
    errors,
    concessionTypes,
    isNewContact,
    page,
    initialValues,
    contactId
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onCancel: page => {
      dispatch(changePhase(page));
    },
    onChangeSuburb: (form: string, item: any, fields: ContactFields) => {
      let postcodeKey = 'postcode';
      let stateKey = 'state';

      fields.headings.forEach(h => {
        h.fields.forEach((f, index) => {
          if (f.key === 'postcode') {
            postcodeKey = toFormKey(f.key, index)
          }
          if (f.key === 'state') {
            stateKey = toFormKey(f.key, index)
          }
        })
      })

      if (item && item.postcode) dispatch(change(form, postcodeKey, item.postcode));
      if (item && item.state) dispatch(change(form, stateKey, item.state));
    },
  };
};

const mergeProps = (stateProps, dispatchProps) => {
  return {
    ...stateProps,  // optional
    ...dispatchProps,  // optional
    onSubmit: (data, dispatch, props): any => {
      return CheckoutService.submitContactDetails(data, props.fields, stateProps.contactId);
    },
  }
}


const Container = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(Form as any);


export default Container;
